"use client";
import { jsx as _jsx } from "react/jsx-runtime";
import { MenuOption, LexicalTypeaheadMenuPlugin, } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createTextNode, $getSelection, $isRangeSelection, KEY_ESCAPE_COMMAND, TextNode, } from 'lexical';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { $createVariableNode } from '../nodes/variable-node';
import { resolveVariable } from './variable-dropdown';
import { VariableOptionItem } from './variable-option-item';
const TRIGGER_REGEX = /\{\{([a-zA-Z0-9_.-]*)$/;
const COMPLETE_VARIABLE_TOKEN_REGEX = /\{\{([a-zA-Z0-9_.-]+)\}\}/;
/**
 * Matches an unfinished variable token at the caret, whatever precedes it.
 * URL templates commonly place variables directly after `=`, `&`, or `/`, so
 * requiring whitespace before `{{` makes keyboard insertion unavailable there.
 */
export function getVariableTypeaheadMatch(text) {
    const match = TRIGGER_REGEX.exec(text);
    if (!match)
        return null;
    return {
        leadOffset: match.index,
        matchingString: match[1],
        replaceableString: match[0],
    };
}
/** Converts a complete manually typed `{{key}}` token into a variable chip. */
export function registerVariableTokenTransform(editor, availableVariables) {
    const enabledKeys = new Set(availableVariables
        .filter((variable) => !variable.disabled && Boolean(variable.value?.trim()))
        .map((variable) => variable.key));
    return editor.registerNodeTransform(TextNode, (textNode) => {
        const text = textNode.getTextContent();
        const match = COMPLETE_VARIABLE_TOKEN_REGEX.exec(text);
        if (!match || !enabledKeys.has(match[1]))
            return;
        const tokenStart = match.index;
        const tokenLength = match[0].length;
        const selection = $getSelection();
        const moveCaretAfterToken = $isRangeSelection(selection) &&
            selection.isCollapsed() &&
            selection.anchor.getNode() === textNode &&
            selection.anchor.offset === tokenStart + tokenLength;
        let tokenTextNode = textNode;
        if (tokenStart > 0)
            tokenTextNode = textNode.splitText(tokenStart)[1];
        if (tokenTextNode.getTextContentSize() > tokenLength) {
            tokenTextNode = tokenTextNode.splitText(tokenLength)[0];
        }
        const variableNode = $createVariableNode(match[1]);
        tokenTextNode.replace(variableNode);
        if (moveCaretAfterToken)
            variableNode.selectNext(0, 0);
    });
}
class VariableTypeaheadOption extends MenuOption {
    constructor(variable, displayLabel) {
        super(variable.key);
        this.variable = variable;
        this.displayLabel = displayLabel;
    }
}
const MENU_STYLE = {
    position: 'absolute',
    zIndex: 10001,
    background: 'var(--chakra-colors-bg)',
    border: '1px solid var(--chakra-colors-border)',
    borderRadius: 'var(--chakra-radii-md)',
    boxShadow: 'var(--chakra-shadows-md)',
    maxHeight: '240px',
    overflowY: 'auto',
    minWidth: '240px',
    padding: 'var(--chakra-spacing-1)',
};
const ITEM_STYLE = {
    display: 'flex',
    alignItems: 'center',
    padding: 'var(--chakra-spacing-1) var(--chakra-spacing-2)',
    cursor: 'pointer',
    borderRadius: 'var(--chakra-radii-sm)',
    transition: 'background 0.1s',
};
function computeMenuPosition(anchorRect, portalTarget) {
    if (portalTarget === document.body) {
        return {
            top: anchorRect.bottom + window.scrollY + 4,
            left: anchorRect.left + window.scrollX,
        };
    }
    const targetRect = portalTarget.getBoundingClientRect();
    return {
        top: anchorRect.bottom - targetRect.top + portalTarget.scrollTop + 4,
        left: anchorRect.left - targetRect.left + portalTarget.scrollLeft,
    };
}
export function VariableTypeaheadPlugin({ availableVariables, translateFn, }) {
    const [queryString, setQueryString] = useState(null);
    const [editor] = useLexicalComposerContext();
    useEffect(() => registerVariableTokenTransform(editor, availableVariables), [availableVariables, editor]);
    const triggerFn = useCallback(getVariableTypeaheadMatch, []);
    const options = useMemo(() => {
        const q = (queryString ?? '').toLowerCase();
        return availableVariables
            .map((v) => ({ variable: v, resolved: resolveVariable(v, translateFn) }))
            .filter(({ resolved }) => !q || resolved.key.toLowerCase().includes(q) || resolved.label.toLowerCase().includes(q))
            .map(({ variable, resolved }) => new VariableTypeaheadOption(variable, resolved.label));
    }, [availableVariables, queryString, translateFn]);
    const handleSelectOption = useCallback((option, textNodeContainingQuery, closeMenu) => {
        if (textNodeContainingQuery) {
            const selection = $getSelection();
            if ($isRangeSelection(selection) && selection.isCollapsed()) {
                const triggerIdx = textNodeContainingQuery.getTextContent().lastIndexOf('{{');
                if (triggerIdx >= 0) {
                    const nodeToReplace = triggerIdx > 0
                        ? textNodeContainingQuery.splitText(triggerIdx)[1]
                        : textNodeContainingQuery;
                    if (nodeToReplace) {
                        const variableNode = $createVariableNode(option.variable.key);
                        nodeToReplace.replace(variableNode);
                        // Trailing space so the next typed character doesn't concatenate
                        // visually with the chip.
                        const spaceNode = $createTextNode(' ');
                        variableNode.insertAfter(spaceNode);
                        spaceNode.select(1, 1);
                    }
                }
            }
        }
        closeMenu();
    }, []);
    const menuRef = useRef(null);
    const positionRef = useRef(null);
    const prevQueryRef = useRef(null);
    // Portal inside the nearest dialog so the menu sits above the modal overlay.
    const [portalTarget, setPortalTarget] = useState(null);
    useEffect(() => {
        const rootEl = editor.getRootElement();
        if (!rootEl)
            return;
        const dialog = rootEl.closest('[data-scope="dialog"][data-part="content"]');
        setPortalTarget(dialog || document.body);
    }, [editor]);
    // Click-outside dismissal. LexicalTypeaheadMenuPlugin has no built-in handling.
    useEffect(() => {
        const handleMouseDown = (e) => {
            if (!menuRef.current || menuRef.current.contains(e.target))
                return;
            editor.dispatchCommand(KEY_ESCAPE_COMMAND, new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true }));
        };
        document.addEventListener('mousedown', handleMouseDown, true);
        return () => document.removeEventListener('mousedown', handleMouseDown, true);
    }, [editor]);
    return (_jsx(LexicalTypeaheadMenuPlugin, { onQueryChange: setQueryString, onSelectOption: handleSelectOption, triggerFn: triggerFn, options: options, anchorClassName: "variable-typeahead-anchor", menuRenderFn: (anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex, options: menuOptions }) => {
            if (menuOptions.length === 0 || !anchorElementRef.current || !portalTarget) {
                return null;
            }
            // Reset cached position when query changes (menu reopened or trigger moved).
            if (queryString !== prevQueryRef.current) {
                positionRef.current = null;
                prevQueryRef.current = queryString;
            }
            // Cache position once the anchor is properly placed (height > 0).
            // Lexical repositions the anchor on re-renders which causes jumps on hover.
            const anchorRect = anchorElementRef.current.getBoundingClientRect();
            if (!positionRef.current && anchorRect.height > 0) {
                positionRef.current = computeMenuPosition(anchorRect, portalTarget);
            }
            const pos = positionRef.current ?? computeMenuPosition(anchorRect, portalTarget);
            return createPortal(_jsx("div", { ref: menuRef, "data-scope": "menu", "data-part": "content", style: { ...MENU_STYLE, top: `${pos.top}px`, left: `${pos.left}px` }, children: menuOptions.map((option, index) => (_jsx("div", { ref: (el) => option.setRefElement(el), role: "option", "aria-selected": selectedIndex === index, tabIndex: -1, style: {
                        ...ITEM_STYLE,
                        background: selectedIndex === index ? 'var(--chakra-colors-bg-muted)' : 'transparent',
                    }, onMouseEnter: () => setHighlightedIndex(index), onMouseDown: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        selectOptionAndCleanUp(option);
                    }, children: _jsx(VariableOptionItem, { label: option.displayLabel, variableKey: option.variable.key, isSelected: selectedIndex === index, pointerEventsNone: true }) }, option.key))) }), portalTarget);
        } }));
}
